import { Autocomplete, IconButton, Stack, TextField } from '@mui/material';
import { getSelectorAttribute, createPlanPageSelectors } from '@vy/app-selectors';
import { VyIcon } from '@vy/ui-design-system';
import type { CatalogProducts, ObjectId, UsageProductVariantInput } from '@vy/types';
import type { Nullable } from '@vy/utils';
import { isMultiple, isNotEmptyArray, isNotNullOrUndefined, isSingleItem, purge, removeInIndex, withDefault } from '@vy/utils';
import { defined } from '@vy/null';
import { flatMap } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext, get, Controller } from 'react-hook-form';
import { useFormErrorNotificationContext } from '../../../../../../contexts/form-error-notification-context/form-error-notification-context';
import { useMeters } from '../../../../../../hooks/requests/use-meters/use-meters';
import { useCatalogProductsList } from '../../../../../../hooks/requests/use-catalog-products-list';
import { vyFormRegister } from '../../../../../../utils/react-hook-forms-utils';
import { useUsagePricingOption } from '../../../create-plan-page-hooks/use-usage-pricing-option';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';
import type { CreatePlanTab } from '../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { CREATE_PLAN_TABS } from '../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import type { SelectedProductNameType } from './PerUnitUsageCharge/per-unit-usage-charge-components/PerUnitUsageChargeForm';
import { ProductSourceSelect } from './ProductSourceSelect/ProductSourceSelect';
import {
  getAllowedUsagePricingOptions,
  getUsagePricingModelValue,
} from '../../../create-plan-page-services/usage-pricing-model-value-provider';
import { creditTypeIdsFromSource } from '../../shared/credit-type-ids-from-source';

export type UsageProductVariantHeaderEditProps = {
  productIndex: number;
  productVariant: UsageProductVariantInput;
  productVariantIndex: number;
};

export const UsageProductVariantHeaderEdit: React.FC<UsageProductVariantHeaderEditProps> = ({
  productIndex,
  productVariant,
  productVariantIndex,
}) => {
  const { registerErrorField } = useFormErrorNotificationContext<PlanInputForm, CreatePlanTab>();
  const {
    watch,
    register,
    control,
    getFieldState,
    setValue,
    formState: { errors },
    getValues,
  } = useFormContext<PlanInputForm>();
  const [selectedProductName, setSelectedProductName] = useState<SelectedProductNameType>({
    isPreviousMeterName: false,
    value: '',
  });
  const {
    data: { meters },
  } = useMeters();
  const catalogProductsQuery = useCatalogProductsList({ enabled: true });
  const onUsagePricingOptionChange = useUsagePricingOption(productIndex, productVariantIndex);

  const nameRegister = register(`usageProducts.${productIndex}.variants.${productVariantIndex}.name`);
  const descriptionRegister = vyFormRegister(
    register,
    `usageProducts.${productIndex}.variants.${productVariantIndex}.description`,
  );
  const productVariants = getValues(`usageProducts.${productIndex}.variants`);
  const registeredProducts = watch('usageProducts');
  const selectedMetersIds = purge(
    flatMap(registeredProducts, ({ variants }) => variants.map(({ meterId }) => meterId)),
  );

  const onDelete = useCallback(() => {
    const updatedProductVariants = removeInIndex(productVariants, productVariantIndex);
    setValue(`usageProducts.${productIndex}.variants`, updatedProductVariants);

    // Dropping back to a single variant un-groups the product, so clear the
    // group name — otherwise it lingers and is shown on invoices (ENG-6069).
    if (isSingleItem(updatedProductVariants)) {
      setValue(`usageProducts.${productIndex}.name`, undefined);
    }
  }, [productIndex, productVariantIndex, productVariants]);

  const canDelete = isMultiple(productVariants);
  registerErrorField(
    `usageProducts.${productIndex}.variants.${productVariantIndex}`,
    CREATE_PLAN_TABS.tierAndVolumePricing,
  );
  registerErrorField(nameRegister.name, CREATE_PLAN_TABS.tierAndVolumePricing);
  registerErrorField(descriptionRegister.name, CREATE_PLAN_TABS.tierAndVolumePricing);

  function handleMeterSelect(newMeterId: Nullable<ObjectId>) {
    const meter = meters.find(({ id }) => id === newMeterId);
    setValue(`usageProducts.${productIndex}.creditTypeIds`, creditTypeIdsFromSource(meter));

    if (selectedProductName.isPreviousMeterName || selectedProductName.value === '') {
      const meterName = withDefault(meter?.name, '');
      setSelectedProductName({ isPreviousMeterName: true, value: meterName });
      setValue(`usageProducts.${productIndex}.variants.${productVariantIndex}.name`, meterName);
    }

    syncCatalogProductErp(meter?.name);
  }

  function syncCatalogProductErp(meterName: string | undefined) {
    if (!defined(meterName)) {
      setValue(`usageProducts.${productIndex}.catalogProductId`, undefined);
      setValue(`usageProducts.${productIndex}.integrationEntities`, undefined);
      setValue(`usageProducts.${productIndex}.externalIntegration`, undefined);
      return;
    }

    const catalogProducts = catalogProductsQuery.data?.catalogProducts ?? [];
    const companionCatalogProduct = catalogProducts.find(
      (catalogProduct: CatalogProducts) => catalogProduct.name === meterName,
    );

    if (!isNotNullOrUndefined(companionCatalogProduct)) {
      setValue(`usageProducts.${productIndex}.catalogProductId`, undefined);
      setValue(`usageProducts.${productIndex}.integrationEntities`, undefined);
      setValue(`usageProducts.${productIndex}.externalIntegration`, undefined);
      return;
    }

    setValue(`usageProducts.${productIndex}.catalogProductId`, companionCatalogProduct.id);

    if (isNotEmptyArray(companionCatalogProduct.integrationEntities)) {
      setValue(`usageProducts.${productIndex}.integrationEntities`, companionCatalogProduct.integrationEntities);
    }

    if (isNotNullOrUndefined(companionCatalogProduct.externalIntegration)) {
      setValue(`usageProducts.${productIndex}.externalIntegration`, {
        type: companionCatalogProduct.externalIntegration.type,
        id: companionCatalogProduct.externalIntegration.id,
      });
    }
  }

  const currentMeterId = watch(`usageProducts.${productIndex}.variants.${productVariantIndex}.meterId`);
  const currentCatalogProductId = watch(`usageProducts.${productIndex}.catalogProductId`);

  useEffect(() => {
    if (defined(currentMeterId) && !defined(currentCatalogProductId) && defined(catalogProductsQuery.data)) {
      const meter = meters.find(({ id }) => id === currentMeterId);
      syncCatalogProductErp(meter?.name);
    }
  }, [currentMeterId, catalogProductsQuery.data]);

  const allowedUsagePricingOptions = getAllowedUsagePricingOptions(productVariants, productVariantIndex);

  return (
    <div className="mb-5 flex flex-wrap justify-between gap-4">
      <Stack
        direction="row"
        sx={{
          width: "100%",
          columnGap: 2
        }}>
        <Autocomplete
          fullWidth
          autoHighlight
          options={allowedUsagePricingOptions}
          renderInput={(params) => <TextField {...params} label="Select pricing" placeholder="Pricing" />}
          value={getUsagePricingModelValue(productVariant)}
          onChange={onUsagePricingOptionChange}
        />
        <Controller
          control={control}
          name={`usageProducts.${productIndex}.variants.${productVariantIndex}.meterId`}
          render={({ field }) => (
            <ProductSourceSelect
              error={isNotNullOrUndefined(
                get(errors, `usageProducts.${productIndex}.variants.${productVariantIndex}.meterId`),
              )}
              helperText={
                getFieldState(`usageProducts.${productIndex}.variants.${productVariantIndex}.meterId`).error
                  ?.message
              }
              defaultValue={productVariant.meterId}
              value={field.value}
              selectedMetersIds={selectedMetersIds}
              onChange={(event, newValue) => {
                event.preventDefault();
                handleMeterSelect(newValue);
                field.onChange(newValue);
              }}
            />
          )}
        />

        <TextField
          size="medium"
          label="Product name"
          placeholder="Text here"
          {...nameRegister}
          defaultValue={getValues(nameRegister.name)}
          fullWidth
          {...getSelectorAttribute(createPlanPageSelectors.products.usage.tiers.nameInput)}
          helperText={get(errors, nameRegister.name)?.message}
          error={isNotNullOrUndefined(get(errors, nameRegister.name))}
        />

        {canDelete && (
          <div className="self-center pt-6">
            <IconButton onClick={onDelete}>
              <VyIcon icon="Delete" />
            </IconButton>
          </div>
        )}
      </Stack>
    </div>
  );
};
